import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Grid,
    Divider as MuiDivider,
    styled,
    Tab,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { Link, Outlet, useMatch, useNavigate } from 'react-router'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import BlurLinearIcon from '@mui/icons-material/BlurLinear'
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart'
import CasesIcon from '@mui/icons-material/Cases'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'
import IssueFilters from '../../pages/issues/filters/IssueFilters'
import { Helmet } from '@dr.pogodin/react-helmet'
import { spacing } from '@mui/system'
import { TabContext, TabList, TabPanel } from '@mui/lab'

const Divider = styled(MuiDivider)(spacing)

const StatisticsView = () => {
    const { isLoading, result: fetchedIssues } = useFetchEntitiesWithCache('issue')
    const navigate = useNavigate()
    const match = useMatch('statistics/:section')
    const [isVisible, setIsVisible] = useState(false)
    const defaultPath = 'trends'
    const currentTab = match?.params.section || defaultPath
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    useEffect(() => {
        const lsId = 'wizard_stats'
        const shouldShow = !window.localStorage.getItem(lsId)
        setIsVisible(shouldShow)

        if (shouldShow) {
            window.localStorage.setItem(lsId, 'true')
        }
    }, [setIsVisible])

    useEffect(() => {
        if (!match?.params.section) {
            navigate('./' + defaultPath, { replace: true })
        }
    }, [match, navigate])

    const handleClickGo = () => {
        setIsVisible(false)
    }

    return (
        <>
            <Helmet title='Statistik' />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Statistik
            </Typography>
            <Divider my={6} />
            <IssueFilters isLoading={isLoading} issues={fetchedIssues} offeredSizeMinValue={1} />
            {isVisible && (
                <Grid container>
                    <Grid size={{ xs: 12, lg: 6 }} offset={{ xs: 0, lg: 3 }}>
                        <Card sx={{ padding: 6, marginBottom: 6 }}>
                            <CardContent>
                                <Typography variant='h5' component='div'>
                                    Statistik i Finance Lab
                                </Typography>
                                <p>Genom statistikverktyget kan du filtrera, visualisera och analysera data.</p>
                                <p>Inledningsvis har vi introducerat ett par olika graftyper:</p>
                                <ul>
                                    <li>Trendgrafer och matriser för företrädesemissioner</li>
                                    <li>Samt en överblick över emissionsmarknaden</li>
                                </ul>
                                <p>
                                    Filtreringarna ovan påverkar vilket innehåll som finns i graferna och tabellerna.
                                    Ställ in den tidsperiod och de inställningar som är relevanta - experiment
                                    uppmuntras.
                                </p>
                                <p>
                                    Hör gärna av dig till din kontaktperson hos Finance Lab med feedback och
                                    funderingar!
                                </p>
                            </CardContent>
                            <CardActions>
                                <Button onClick={handleClickGo} variant='contained'>
                                    Kom igång
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                </Grid>
            )}
            <Card>
                <CardContent>
                    <TabContext value={currentTab}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 6 }}>
                            <TabList>
                                <Tab
                                    LinkComponent={Link}
                                    label='Trendgrafer'
                                    icon={<QueryStatsIcon />}
                                    iconPosition='start'
                                    sx={{
                                        minHeight: 'auto',
                                    }}
                                    to='./trends'
                                    value='trends'
                                />
                                <Tab
                                    LinkComponent={Link}
                                    label='Matriser'
                                    icon={<BlurLinearIcon />}
                                    iconPosition='start'
                                    sx={{
                                        minHeight: 'auto',
                                    }}
                                    to='./scatter-plots'
                                    value='scatter-plots'
                                />
                                <Tab
                                    LinkComponent={Link}
                                    label='Emissionsmarknaden'
                                    icon={<StackedBarChartIcon />}
                                    iconPosition='start'
                                    sx={{
                                        minHeight: 'auto',
                                    }}
                                    to='./issue-market'
                                    value='issue-market'
                                />
                                <Tab
                                    LinkComponent={Link}
                                    label='Rådgivare'
                                    icon={<CasesIcon />}
                                    iconPosition='start'
                                    sx={{
                                        minHeight: 'auto',
                                    }}
                                    to='./advisors'
                                    value='advisors'
                                />
                            </TabList>
                        </Box>
                        <TabPanel value={currentTab} sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                            <Outlet />
                        </TabPanel>
                    </TabContext>
                </CardContent>
            </Card>
        </>
    )
}

export default StatisticsView
